import { briefApiClient as apiClient } from "./client";

// ─── Types (mirror serverless services/core/models/brief.ts) ─────────────────

export type BriefRole = "owner" | "admin" | "member" | "guest";
export type BriefPriority = "none" | "low" | "medium" | "high" | "urgent";
export type BriefProjectStatus = "active" | "on_hold" | "completed" | "archived";
export type BriefProjectHealth = "on_track" | "at_risk" | "blocked" | "completed";
export type BriefRecurrence = "daily" | "weekly" | "monthly";
export type BriefApprovalStatus = "pending" | "approved" | "rejected";

export interface BriefWorkspace {
  id: string; name: string; icon?: string; description?: string;
  ownerId: string; role?: BriefRole; createdAt: number; updatedAt: number;
}
export interface BriefMember { userId: string; role: BriefRole; teamIds?: string[]; roleIds?: string[]; createdAt: number; }
export interface BriefProject {
  id: string; workspaceId: string; name: string; description?: string;
  icon?: string; color?: string; status: BriefProjectStatus; health?: BriefProjectHealth;
  ownerId: string; startDate?: string; dueDate?: string; createdAt: number; updatedAt: number;
}
export interface BriefBoard {
  id: string; projectId: string; workspaceId: string; name: string;
  settings?: Record<string, unknown>; createdAt: number; updatedAt: number;
}
export interface BriefList {
  id: string; boardId: string; name: string; order: string;
  wipLimit?: number; archived?: boolean; createdAt: number; updatedAt: number;
}
export interface BriefLabelRef { id: string; name: string; color: string; }
export interface BriefCardLink { url: string; label: string; }
export interface BriefChecklistSubItem {
  id: string;
  type: "description" | "link" | "attachment";
  text?: string;
  url?: string;
  label?: string;
  name?: string;
  contentType?: string;
  size?: number;
}
export interface BriefChecklistItem {
  id: string; title: string; checked: boolean; order: string; assigneeId?: string; dueDate?: string;
  subItems?: BriefChecklistSubItem[];
}
export interface BriefCard {
  id: string; boardId: string; listId: string; projectId: string; workspaceId: string;
  title: string; description?: string; order: string; priority: BriefPriority;
  labels?: BriefLabelRef[]; startDate?: string; dueDate?: string; checklist?: BriefChecklistItem[];
  assigneeIds?: string[]; milestoneId?: string; customFields?: Record<string, unknown>;
  estimateMinutes?: number; loggedMinutes?: number; completedAt?: number; recurrence?: BriefRecurrence;
  approvalStatus?: BriefApprovalStatus; approverId?: string; links?: BriefCardLink[]; categoryId?: string;
  hiddenSections?: string[];
  createdBy: string; createdAt: number; updatedAt: number;
}
export interface BriefComment {
  id: string; cardId: string; authorId: string; body: string;
  mentions?: string[]; parentId?: string; resolved?: boolean; createdAt: number; updatedAt: number;
}
export interface BriefActivity {
  id: string; cardId: string; actorId: string; action: string;
  meta?: Record<string, unknown>; createdAt: number;
}
export interface BriefAttachment {
  id: string; cardId: string; name: string; url: string;
  contentType?: string; size?: number; uploadedBy: string; createdAt: number;
}
export interface BriefLabel { id: string; projectId: string; name: string; color: string; }
export type BriefCustomFieldType = "text" | "number" | "select" | "multi" | "date" | "checkbox" | "currency" | "url";
export interface BriefCustomField { id: string; projectId: string; name: string; type: BriefCustomFieldType; options?: string[]; }
export interface BriefMilestone {
  id: string; projectId: string; name: string; description?: string;
  dueDate?: string; completedAt?: number; createdAt: number; updatedAt: number;
}
export interface BriefCategory { id: string; boardId: string; name: string; color: string; icon: string; order: string; }
export interface BriefBoardSnapshot { board: BriefBoard; lists: BriefList[]; cards: BriefCard[]; categories: BriefCategory[]; }
export interface BriefMyWorkItem {
  cardId: string; title: string; dueDate?: string;
  boardId: string; projectId: string; workspaceId: string; role: string;
}

// ─── Workspaces ──────────────────────────────────────────────────────────────

export const listWorkspaces = () => apiClient.get<BriefWorkspace[]>("/api/brief/workspaces");
export const getWorkspace = (id: string) => apiClient.get<BriefWorkspace>(`/api/brief/workspaces/${id}`);
export const createWorkspace = (data: { name: string; icon?: string; description?: string }) =>
  apiClient.post<BriefWorkspace>("/api/brief/workspaces", data);
export const updateWorkspace = (id: string, data: Partial<Pick<BriefWorkspace, "name" | "icon" | "description">>) =>
  apiClient.patch<BriefWorkspace>(`/api/brief/workspaces/${id}`, data);
export const deleteWorkspace = (id: string) => apiClient.delete(`/api/brief/workspaces/${id}`);

// ─── Members & invites ────────────────────────────────────────────────────────

export const listBriefMembers = (wsId: string) => apiClient.get<BriefMember[]>(`/api/brief/workspaces/${wsId}/members`);
export interface BriefAuditEvent { id: string; workspaceId: string; actorId: string; action: string; meta?: Record<string, unknown>; createdAt: number; }
export const listBriefAudit = (wsId: string) => apiClient.get<BriefAuditEvent[]>(`/api/brief/workspaces/${wsId}/audit`);
export interface BriefTeam { id: string; workspaceId: string; name: string; memberIds: string[]; roleIds?: string[]; createdAt: number; updatedAt: number; }
export const listTeams = (wsId: string) => apiClient.get<BriefTeam[]>(`/api/brief/workspaces/${wsId}/teams`);
export const createTeam = (wsId: string, data: { name: string; memberIds?: string[] }) => apiClient.post<BriefTeam>(`/api/brief/workspaces/${wsId}/teams`, data);
export const updateTeam = (id: string, data: { name?: string; memberIds?: string[]; roleIds?: string[] }) => apiClient.patch<BriefTeam>(`/api/brief/teams/${id}`, data);
export const deleteTeam = (id: string) => apiClient.delete(`/api/brief/teams/${id}`);
export const updateBriefMemberRole = (wsId: string, userId: string, role: BriefRole) =>
  apiClient.patch<{ userId: string; role: BriefRole }>(`/api/brief/workspaces/${wsId}/members/${userId}`, { role });
export const removeBriefMember = (wsId: string, userId: string) =>
  apiClient.delete(`/api/brief/workspaces/${wsId}/members/${userId}`);
export const createBriefInvite = (wsId: string, data: { email?: string; role?: BriefRole }) =>
  apiClient.post<{ token: string; role: BriefRole; link: string }>(`/api/brief/workspaces/${wsId}/invites`, data);
export const getBriefInvite = (token: string) =>
  apiClient.get<{ token: string; role: BriefRole; workspace: { id: string; name: string; icon?: string } | null }>(
    `/api/brief/invites/${token}`
  );
export const acceptBriefInvite = (token: string) => apiClient.post<BriefWorkspace>(`/api/brief/invites/${token}/accept`, {});

// ─── Projects ──────────────────────────────────────────────────────────────────

export const listProjects = (wsId: string) => apiClient.get<BriefProject[]>(`/api/brief/workspaces/${wsId}/projects`);
export const getProject = (id: string) => apiClient.get<BriefProject>(`/api/brief/projects/${id}`);
export const createProject = (
  wsId: string,
  data: { name: string; description?: string; icon?: string; color?: string; startDate?: string; dueDate?: string }
) => apiClient.post<BriefProject>(`/api/brief/workspaces/${wsId}/projects`, data);
export const updateProject = (id: string, data: Partial<Omit<BriefProject, "id" | "workspaceId" | "createdAt" | "updatedAt">>) =>
  apiClient.patch<BriefProject>(`/api/brief/projects/${id}`, data);
export const deleteProject = (id: string) => apiClient.delete(`/api/brief/projects/${id}`);

// ─── Boards ────────────────────────────────────────────────────────────────────

export const listBoards = (projectId: string) => apiClient.get<BriefBoard[]>(`/api/brief/projects/${projectId}/boards`);
export const getBoard = (id: string) => apiClient.get<BriefBoard>(`/api/brief/boards/${id}`);
export const getBoardSnapshot = (id: string) => apiClient.get<BriefBoardSnapshot>(`/api/brief/boards/${id}/snapshot`);
export const getBoardTrash = (id: string) => apiClient.get<{ cards: BriefCard[]; lists: BriefList[] }>(`/api/brief/boards/${id}/trash`);
export const listBriefCategories = (boardId: string) => apiClient.get<BriefCategory[]>(`/api/brief/boards/${boardId}/categories`);
export const createBriefCategory = (boardId: string, data: { name: string; color: string; icon: string }) => apiClient.post<BriefCategory>(`/api/brief/boards/${boardId}/categories`, data);
export const updateBriefCategory = (id: string, data: { name?: string; color?: string; icon?: string }) => apiClient.patch<BriefCategory>(`/api/brief/categories/${id}`, data);
export const deleteBriefCategory = (id: string) => apiClient.delete(`/api/brief/categories/${id}`);
export const createBoardShare = (id: string) => apiClient.post<{ token: string; url: string }>(`/api/brief/boards/${id}/share`, {});
export const getPublicBoard = (token: string) =>
  apiClient.get<{ board: BriefBoard; lists: BriefList[]; cards: BriefCard[]; readOnly: boolean }>(`/api/brief/public/board?token=${encodeURIComponent(token)}`);
export const createBoard = (projectId: string, data: { name: string; settings?: Record<string, unknown> }) =>
  apiClient.post<BriefBoard>(`/api/brief/projects/${projectId}/boards`, data);
export const updateBoard = (id: string, data: { name?: string; settings?: Record<string, unknown> }) =>
  apiClient.patch<BriefBoard>(`/api/brief/boards/${id}`, data);
export const deleteBoard = (id: string) => apiClient.delete(`/api/brief/boards/${id}`);

// ─── Lists ───────────────────────────────────────────────────────────────────

export const createList = (boardId: string, data: { name: string; order?: string; wipLimit?: number }) =>
  apiClient.post<BriefList>(`/api/brief/boards/${boardId}/lists`, data);
export const updateList = (id: string, data: { name?: string; order?: string; wipLimit?: number | null; archived?: boolean }) =>
  apiClient.patch<BriefList>(`/api/brief/lists/${id}`, data);
export const deleteList = (id: string) => apiClient.delete(`/api/brief/lists/${id}`);

// ─── Cards ───────────────────────────────────────────────────────────────────

export const getCard = (id: string) => apiClient.get<BriefCard>(`/api/brief/cards/${id}`);
export const createCard = (
  boardId: string,
  data: { listId: string; title: string; description?: string; order?: string; priority?: BriefPriority; labels?: BriefLabelRef[]; startDate?: string; dueDate?: string; assigneeIds?: string[]; categoryId?: string }
) => apiClient.post<BriefCard>(`/api/brief/boards/${boardId}/cards`, data);
export interface UpdateCardInput {
  title?: string; description?: string | null; priority?: BriefPriority; labels?: BriefLabelRef[];
  startDate?: string | null; dueDate?: string | null; checklist?: BriefChecklistItem[];
  milestoneId?: string | null; customFields?: Record<string, unknown>;
  estimateMinutes?: number | null; loggedMinutes?: number | null; completed?: boolean;
  recurrence?: BriefRecurrence | null;
  approvalStatus?: BriefApprovalStatus | null;
  links?: BriefCardLink[];
  categoryId?: string | null;
  hiddenSections?: string[];
}
export const updateCard = (id: string, data: UpdateCardInput) => apiClient.patch<BriefCard>(`/api/brief/cards/${id}`, data);
export const moveCard = (id: string, data: { listId: string; order: string }) =>
  apiClient.post<BriefCard>(`/api/brief/cards/${id}/move`, data);
export const deleteCard = (id: string) => apiClient.delete(`/api/brief/cards/${id}`);
export const restoreCard = (id: string, boardId: string) => apiClient.post<BriefCard>(`/api/brief/cards/${id}/restore`, { boardId });
export const setCardAssignees = (id: string, userIds: string[]) =>
  apiClient.post<BriefCard>(`/api/brief/cards/${id}/assignees`, { userIds });
export const generateAiTasks = (boardId: string, brief: string, listId: string) =>
  apiClient.post<{ created: number; cards: BriefCard[] }>(`/api/brief/boards/${boardId}/ai-tasks`, { brief, listId });
export const getGithubIntegration = (wsId: string) => apiClient.get<{ configured: boolean; repo: string }>(`/api/brief/workspaces/${wsId}/integrations/github`);
export const putGithubIntegration = (wsId: string, data: { token: string; repo: string }) => apiClient.put<{ configured: boolean; repo: string }>(`/api/brief/workspaces/${wsId}/integrations/github`, data);
export const createGithubIssue = (cardId: string) => apiClient.post<BriefCard>(`/api/brief/cards/${cardId}/github-issue`, {});
export const putGcalConfig = (wsId: string, data: { clientId: string; clientSecret: string }) =>
  apiClient.put<{ configured: boolean }>(`/api/brief/workspaces/${wsId}/integrations/gcal`, data);
export const getGcalConnectUrl = (wsId: string) => apiClient.get<{ url: string }>(`/api/brief/gcal/connect?wsId=${encodeURIComponent(wsId)}`);
export const addCardToCalendar = (cardId: string) => apiClient.post<{ ok: boolean; link: string | null }>(`/api/brief/cards/${cardId}/calendar`, {});

// ─── Comments / activity / attachments ─────────────────────────────────────────

export const listComments = (cardId: string) => apiClient.get<BriefComment[]>(`/api/brief/cards/${cardId}/comments`);
export const createComment = (cardId: string, data: { body: string; mentions?: string[]; parentId?: string }) =>
  apiClient.post<BriefComment>(`/api/brief/cards/${cardId}/comments`, data);
export const updateComment = (id: string, data: { body?: string; resolved?: boolean }) =>
  apiClient.patch<BriefComment>(`/api/brief/comments/${id}`, data);
export const deleteComment = (id: string) => apiClient.delete(`/api/brief/comments/${id}`);
export const listActivity = (cardId: string) => apiClient.get<BriefActivity[]>(`/api/brief/cards/${cardId}/activity`);

export type BriefDependencyType = "blocks" | "blocked_by" | "related";
export interface BriefDependency { sourceCardId: string; targetCardId: string; type: BriefDependencyType; direction: "outgoing" | "incoming"; }
export const listDependencies = (cardId: string) => apiClient.get<BriefDependency[]>(`/api/brief/cards/${cardId}/dependencies`);
export const addDependency = (cardId: string, targetCardId: string, type: BriefDependencyType) =>
  apiClient.post(`/api/brief/cards/${cardId}/dependencies`, { targetCardId, type });
export const removeDependency = (cardId: string, targetCardId: string, type: BriefDependencyType) =>
  apiClient.delete(`/api/brief/cards/${cardId}/dependencies?target=${encodeURIComponent(targetCardId)}&type=${type}`);
export const listAttachments = (cardId: string) => apiClient.get<BriefAttachment[]>(`/api/brief/cards/${cardId}/attachments`);
export const createAttachment = (cardId: string, data: { name: string; url: string; contentType?: string; size?: number }) =>
  apiClient.post<BriefAttachment>(`/api/brief/cards/${cardId}/attachments`, data);
export const deleteAttachment = (id: string) => apiClient.delete(`/api/brief/attachments/${id}`);

// ─── Labels & milestones ───────────────────────────────────────────────────────

export const listLabels = (projectId: string) => apiClient.get<BriefLabel[]>(`/api/brief/projects/${projectId}/labels`);
export const createLabel = (projectId: string, data: { name: string; color: string }) =>
  apiClient.post<BriefLabel>(`/api/brief/projects/${projectId}/labels`, data);
export const updateLabel = (id: string, data: { name?: string; color?: string }) =>
  apiClient.patch<BriefLabel>(`/api/brief/labels/${id}`, data);
export const deleteLabel = (id: string) => apiClient.delete(`/api/brief/labels/${id}`);
export const listCustomFields = (projectId: string) => apiClient.get<BriefCustomField[]>(`/api/brief/projects/${projectId}/fields`);
export const createCustomField = (projectId: string, data: { name: string; type: BriefCustomFieldType; options?: string[] }) =>
  apiClient.post<BriefCustomField>(`/api/brief/projects/${projectId}/fields`, data);
export const deleteCustomField = (id: string) => apiClient.delete(`/api/brief/fields/${id}`);

export const listMilestones = (projectId: string) => apiClient.get<BriefMilestone[]>(`/api/brief/projects/${projectId}/milestones`);
export const createMilestone = (projectId: string, data: { name: string; description?: string; dueDate?: string }) =>
  apiClient.post<BriefMilestone>(`/api/brief/projects/${projectId}/milestones`, data);
export const updateMilestone = (id: string, data: { name?: string; description?: string | null; dueDate?: string | null; completed?: boolean }) =>
  apiClient.patch<BriefMilestone>(`/api/brief/milestones/${id}`, data);
export const deleteMilestone = (id: string) => apiClient.delete(`/api/brief/milestones/${id}`);

// ─── Cross-cutting ─────────────────────────────────────────────────────────────

export interface BriefDoc {
  id: string; projectId: string; title: string; content: string;
  parentId?: string; order: string; createdAt: number; updatedAt: number;
}
export const listDocs = (projectId: string) => apiClient.get<BriefDoc[]>(`/api/brief/projects/${projectId}/docs`);
export const createDoc = (projectId: string, data: { title: string; content?: string; parentId?: string }) =>
  apiClient.post<BriefDoc>(`/api/brief/projects/${projectId}/docs`, data);
export const updateDoc = (id: string, data: { title?: string; content?: string; parentId?: string | null }) =>
  apiClient.patch<BriefDoc>(`/api/brief/docs/${id}`, data);
export const deleteDoc = (id: string) => apiClient.delete(`/api/brief/docs/${id}`);
export interface BriefDocVersion { ts: number; title: string; content: string; authorId: string; }
export const listDocVersions = (id: string) => apiClient.get<BriefDocVersion[]>(`/api/brief/docs/${id}/versions`);

export interface BriefGoal {
  id: string; workspaceId: string; title: string; description?: string;
  targetDate?: string; progress: number; projectIds?: string[]; createdAt: number; updatedAt: number;
}
export const listGoals = (wsId: string) => apiClient.get<BriefGoal[]>(`/api/brief/workspaces/${wsId}/goals`);
export const createGoal = (wsId: string, data: { title: string; description?: string; targetDate?: string; projectIds?: string[] }) =>
  apiClient.post<BriefGoal>(`/api/brief/workspaces/${wsId}/goals`, data);
export const updateGoal = (id: string, data: { title?: string; description?: string | null; targetDate?: string | null; progress?: number; projectIds?: string[] }) =>
  apiClient.patch<BriefGoal>(`/api/brief/goals/${id}`, data);
export const deleteGoal = (id: string) => apiClient.delete(`/api/brief/goals/${id}`);

export type BriefAutomationAction = "set_priority" | "set_complete" | "add_label" | "assign";
export interface BriefRule {
  id: string; boardId: string; name: string; triggerListId: string;
  actionType: BriefAutomationAction; actionValue?: string; enabled: boolean;
}
export const listRules = (boardId: string) => apiClient.get<BriefRule[]>(`/api/brief/boards/${boardId}/rules`);
export const createRule = (boardId: string, data: { name: string; triggerListId: string; actionType: BriefAutomationAction; actionValue?: string }) =>
  apiClient.post<BriefRule>(`/api/brief/boards/${boardId}/rules`, data);
export const deleteRule = (boardId: string, id: string) => apiClient.delete(`/api/brief/boards/${boardId}/rules/${id}`);

export interface BriefTemplate { id: string; workspaceId: string; name: string; lists: string[]; createdAt: number; updatedAt: number; }
export const listTemplates = (wsId: string) => apiClient.get<BriefTemplate[]>(`/api/brief/workspaces/${wsId}/templates`);
export const createTemplate = (wsId: string, data: { name: string; lists: string[] }) => apiClient.post<BriefTemplate>(`/api/brief/workspaces/${wsId}/templates`, data);
export const deleteTemplate = (wsId: string, id: string) => apiClient.delete(`/api/brief/workspaces/${wsId}/templates/${id}`);

export interface BriefWebhook { id: string; workspaceId: string; url: string; events: string[]; createdAt: number; }
export const listWebhooks = (wsId: string) => apiClient.get<BriefWebhook[]>(`/api/brief/workspaces/${wsId}/webhooks`);
export const createWebhook = (wsId: string, data: { url: string; events: string[] }) => apiClient.post<BriefWebhook>(`/api/brief/workspaces/${wsId}/webhooks`, data);
export const deleteWebhook = (wsId: string, id: string) => apiClient.delete(`/api/brief/workspaces/${wsId}/webhooks/${id}`);

export interface BriefNotifPrefs { push: boolean; mutedTypes: string[]; }
export const getNotifPrefs = () => apiClient.get<BriefNotifPrefs>("/api/brief/notif-prefs");
export const putNotifPrefs = (data: Partial<BriefNotifPrefs>) => apiClient.put<BriefNotifPrefs>("/api/brief/notif-prefs", data);

export const BRIEF_PERMISSIONS = [
  "project.create", "project.edit", "project.delete",
  "board.create", "board.edit", "board.delete", "list.manage",
  "card.create", "card.edit", "card.delete", "comment.create",
  "label.manage", "milestone.manage", "category.manage", "doc.manage", "automation.manage",
  "member.manage", "role.manage", "team.manage", "settings.manage", "apikey.manage",
] as const;
export type BriefPermission = (typeof BRIEF_PERMISSIONS)[number];
export interface BriefRoleDef { id: string; workspaceId: string; name: string; color?: string; permissions: BriefPermission[]; isBase?: boolean; baseKey?: BriefRole; }
export const listRoles = (wsId: string) => apiClient.get<BriefRoleDef[]>(`/api/brief/workspaces/${wsId}/roles`);
export const createBriefRole = (wsId: string, data: { name: string; color?: string; permissions?: BriefPermission[] }) => apiClient.post<BriefRoleDef>(`/api/brief/workspaces/${wsId}/roles`, data);
export const updateRoleDef = (wsId: string, roleId: string, data: { name?: string; color?: string; permissions?: BriefPermission[] }) => apiClient.patch<BriefRoleDef>(`/api/brief/workspaces/${wsId}/roles/${roleId}`, data);
export const deleteRoleDef = (wsId: string, roleId: string) => apiClient.delete(`/api/brief/workspaces/${wsId}/roles/${roleId}`);
export const assignUserRoles = (wsId: string, userId: string, roleIds: string[]) => apiClient.post<{ userId: string; roleIds: string[] }>(`/api/brief/workspaces/${wsId}/members/${userId}/roles`, { roleIds });
export const getMyPermissions = (wsId: string) => apiClient.get<{ role: BriefRole; permissions: BriefPermission[] }>(`/api/brief/workspaces/${wsId}/my-permissions`);

export type BriefApiScope = "brief:read" | "brief:write";
export interface BriefApiKey {
  id: string; workspaceId: string; name: string; prefix: string;
  scopes: BriefApiScope[]; createdBy: string; createdAt: number; lastUsedAt?: number;
}
export const listApiKeys = (wsId: string) => apiClient.get<BriefApiKey[]>(`/api/brief/workspaces/${wsId}/api-keys`);
export const createApiKey = (wsId: string, data: { name: string; scopes?: BriefApiScope[] }) =>
  apiClient.post<BriefApiKey & { key: string }>(`/api/brief/workspaces/${wsId}/api-keys`, data);
export const revokeApiKey = (wsId: string, keyId: string) => apiClient.delete(`/api/brief/workspaces/${wsId}/api-keys/${keyId}`);

export interface BriefEnterpriseConfig {
  ssoEnforced: boolean; idpEntityId: string; idpSsoUrl: string; idpCertSet: boolean;
  scimConfigured: boolean; scimUrl: string; scimToken?: string;
}
export const getEnterpriseConfig = (wsId: string) => apiClient.get<BriefEnterpriseConfig>(`/api/brief/workspaces/${wsId}/enterprise`);
export const putEnterpriseConfig = (wsId: string, data: { ssoEnforced?: boolean; idpEntityId?: string; idpSsoUrl?: string; idpCert?: string; rotateScimToken?: boolean }) =>
  apiClient.put<BriefEnterpriseConfig>(`/api/brief/workspaces/${wsId}/enterprise`, data);

export const getMyWork = () => apiClient.get<BriefMyWorkItem[]>("/api/brief/my-work");
export const searchBrief = (workspaceId: string, q: string) =>
  apiClient.get<{ projects: BriefProject[]; cards: BriefCard[] }>(
    `/api/brief/search?workspaceId=${encodeURIComponent(workspaceId)}&q=${encodeURIComponent(q)}`
  );

// Fractional order helper (mirrors serverless orderBetween) for optimistic
// client-side reordering before the server confirms.
const ORDER_DIGITS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";
export function orderBetween(a?: string, b?: string): string {
  const lo = a ?? "";
  const hi = b ?? "";
  let i = 0;
  let result = "";
  while (true) {
    const loCode = i < lo.length ? ORDER_DIGITS.indexOf(lo[i]) : 0;
    const hiCode = i < hi.length ? ORDER_DIGITS.indexOf(hi[i]) : ORDER_DIGITS.length;
    if (loCode === hiCode) { result += ORDER_DIGITS[loCode]; i++; continue; }
    const mid = Math.floor((loCode + hiCode) / 2);
    if (mid === loCode) { result += ORDER_DIGITS[loCode]; i++; continue; }
    result += ORDER_DIGITS[mid];
    return result;
  }
}
